import {
	Check,
	CircleAlert,
	Info,
	Mic,
	Monitor,
	Pause,
	Play,
	RotateCw,
	Share2,
	Square,
	X,
} from 'lucide-react'
import type { CSSProperties } from 'react'
import { useState } from 'react'
import { Trans } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { AudioRecorderState } from '@/hooks/useAudioRecorder'
import type { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import type { MeetingAudioCaptureSupport } from '@/utils/audioRecorderMedia'

type TFunc = ReturnType<typeof useTranslation>['t']

const transStrong = { strong: <strong className="font-semibold text-foreground" /> }

const iconButtonClassName = 'min-h-11 min-w-11 shrink-0 hover:scale-100 active:scale-100'
const textButtonClassName = 'min-h-11 px-5 hover:scale-100 active:scale-100'

interface LevelMeterProps {
	percent: number
	tone: 'live' | 'waiting' | 'idle'
}

function LevelMeter({ percent, tone }: LevelMeterProps) {
	return (
		<div
			className="h-2 min-w-16 flex-1 overflow-hidden rounded-full bg-muted"
			style={{ '--recorder-meter-width': `${percent}%` } as CSSProperties}
			aria-hidden
		>
			<div
				className={cn(
					'h-full w-[var(--recorder-meter-width)] origin-left motion-reduce:duration-500',
					tone === 'idle' ? 'bg-muted-foreground/30' : 'bg-brand-accent',
					'transition-[width] duration-75'
				)}
			/>
		</div>
	)
}

function StatusWord({ tone, children }: { tone: 'live' | 'waiting'; children: string }) {
	return (
		<span
			className={cn(
				'flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-medium',
				tone === 'live' && 'text-brand-accent',
				tone === 'waiting' && 'text-muted-foreground'
			)}
		>
			{tone === 'live' ? (
				<Check className="size-4 shrink-0" aria-hidden />
			) : (
				<CircleAlert className="size-4 shrink-0" aria-hidden />
			)}
			{children}
		</span>
	)
}

export interface AudioRecorderPanelProps {
	t: TFunc
	state: AudioRecorderState
	pauseSupported: boolean
	inputDevices: MediaDeviceInfo[]
	selectedDeviceId: string | null
	onSelectedDeviceIdChange: (id: string | null) => void
	micPreviewConnecting: boolean
	micPreviewFailed: boolean
	micPreviewActive: boolean
	micPreviewReceiving: boolean
	micPreviewMeterPercent: number
	includeMeetingAudio: boolean
	onIncludeMeetingAudioChange: (enabled: boolean) => void
	meetingPreviewConnecting: boolean
	meetingPreviewActive: boolean
	meetingPreviewReceiving: boolean
	meetingPreviewMeterPercent: number
	meetingAudioCaptureSupport: MeetingAudioCaptureSupport
	errorMessage: string | null
	onStartMeetingPreview: () => void
	onStartMicPreview: () => void
	onRecord: () => void
	onPause: () => void
	onResume: () => void
	onStop: () => void
}

export function AudioRecorderPanel({
	t,
	state,
	pauseSupported,
	inputDevices,
	selectedDeviceId,
	onSelectedDeviceIdChange,
	micPreviewConnecting,
	micPreviewFailed,
	micPreviewActive,
	micPreviewReceiving,
	micPreviewMeterPercent,
	includeMeetingAudio,
	onIncludeMeetingAudioChange,
	meetingPreviewConnecting,
	meetingPreviewActive,
	meetingPreviewReceiving,
	meetingPreviewMeterPercent,
	meetingAudioCaptureSupport,
	errorMessage,
	onStartMeetingPreview,
	onStartMicPreview,
	onRecord,
	onPause,
	onResume,
	onStop,
}: AudioRecorderPanelProps) {
	const [isChangingMic, setIsChangingMic] = useState(false)

	const isRecordingPhase = state === 'recording' || state === 'paused' || state === 'requesting'
	const isRecording = state === 'recording'
	const isPaused = state === 'paused'
	const meetingSupported = meetingAudioCaptureSupport.tier !== 'unsupported'
	const micLive = micPreviewActive && !micPreviewFailed
	const showMicSelect = !isRecordingPhase && (!micLive || isChangingMic)
	const selectedDevice = inputDevices.find((d) => d.deviceId === selectedDeviceId)
	const selectedDeviceName = selectedDevice
		? selectedDevice.label.trim() ||
			t('pages.projectDetail.audioRecorder.microphoneUnnamed', {
				index: Math.max(1, inputDevices.indexOf(selectedDevice) + 1),
			})
		: t('pages.projectDetail.audioRecorder.microphoneUnnamed', { index: 1 })

	const showMicError = micPreviewFailed || state === 'error'
	const canRecord =
		micLive &&
		!micPreviewConnecting &&
		state !== 'requesting' &&
		state !== 'error' &&
		(!includeMeetingAudio || meetingPreviewActive)

	const footerReason = showMicError
		? (errorMessage ?? t('pages.projectDetail.audioRecorder.errors.generic'))
		: includeMeetingAudio && !meetingPreviewActive && !isRecordingPhase
			? t('pages.projectDetail.audioRecorder.startRecordingGateHintMeeting')
			: null

	const liveAnnouncement = [
		micLive
			? `${t('pages.projectDetail.audioRecorder.microphoneLabel')} ${t('pages.projectDetail.audioRecorder.microphoneReceiving')}`
			: showMicError
				? (errorMessage ?? t('pages.projectDetail.audioRecorder.errors.generic'))
				: '',
		includeMeetingAudio
			? meetingPreviewActive
				? `${t('pages.projectDetail.audioRecorder.includeMeetingAudioLabel')} ${t('pages.projectDetail.audioRecorder.microphoneReceiving')}`
				: `${t('pages.projectDetail.audioRecorder.includeMeetingAudioLabel')} ${t('pages.projectDetail.audioRecorder.meetingAudioShareGateHint')}`
			: '',
	]
		.filter(Boolean)
		.join('. ')

	const micMeterTone: 'live' | 'waiting' | 'idle' = micLive
		? micPreviewReceiving
			? 'live'
			: 'waiting'
		: 'idle'
	const meetingMeterTone: 'live' | 'waiting' | 'idle' = meetingPreviewActive
		? meetingPreviewReceiving
			? 'live'
			: 'waiting'
		: 'idle'

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="divide-y divide-border px-5">
				<div className="flex items-start gap-3 py-3">
					<Mic className="mt-2 size-4 shrink-0 text-muted-foreground" aria-hidden />
					<div className="min-w-0 flex-1 space-y-2">
						<div className="flex min-h-11 items-center gap-3">
							<span className="shrink-0 text-sm font-medium">
								{t('pages.projectDetail.audioRecorder.microphoneLabel')}
							</span>
							<LevelMeter percent={micLive ? micPreviewMeterPercent : 4} tone={micMeterTone} />
							{micLive && state !== 'error' ? (
								<StatusWord tone="live">
									{t('pages.projectDetail.audioRecorder.microphoneReceiving')}
								</StatusWord>
							) : showMicError ? (
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className={iconButtonClassName}
									onClick={() => void onStartMicPreview()}
									aria-label={t('pages.projectDetail.audioRecorder.startRecordingGateHint')}
								>
									<RotateCw className="size-4" />
								</Button>
							) : null}
						</div>

						{showMicSelect ? (
							<select
								id="audio-input-device"
								className="min-h-11 w-full rounded-lg border border-border bg-background px-3 text-sm"
								value={selectedDeviceId ?? ''}
								disabled={isRecordingPhase}
								aria-label={t('pages.projectDetail.audioRecorder.microphoneLabel')}
								onChange={(e) => {
									const next = e.target.value
									onSelectedDeviceIdChange(next === '' ? null : next)
									setIsChangingMic(false)
								}}
							>
								{inputDevices.map((d, idx) => (
									<option key={d.deviceId} value={d.deviceId}>
										{d.label.trim()
											? d.label
											: t('pages.projectDetail.audioRecorder.microphoneUnnamed', {
													index: idx + 1,
												})}
									</option>
								))}
							</select>
						) : (
							<div className="flex min-h-11 items-center gap-2 text-sm">
								<span className="min-w-0 truncate">{selectedDeviceName}</span>
								{!isRecordingPhase ? (
									<Button
										type="button"
										variant="ghost"
										className={cn(textButtonClassName, 'h-11 px-2 text-muted-foreground')}
										onClick={() => setIsChangingMic(true)}
									>
										{t('pages.projectDetail.audioRecorder.microphoneDefault')}
									</Button>
								) : null}
							</div>
						)}
					</div>
				</div>

				<div className="flex items-start gap-3 py-3">
					<Monitor className="mt-2 size-4 shrink-0 text-muted-foreground" aria-hidden />
					<div className="min-w-0 flex-1 space-y-2">
						<div className="flex min-h-11 items-center gap-3">
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium">
									{t('pages.projectDetail.audioRecorder.includeMeetingAudioLabel')}
								</p>
								{!includeMeetingAudio ? (
									<p className="text-xs text-muted-foreground">
										{t('pages.projectDetail.audioRecorder.includeMeetingAudioDescription')}
									</p>
								) : null}
							</div>
							{includeMeetingAudio ? (
								<>
									{meetingPreviewActive ? (
										<StatusWord tone="live">
											{t('pages.projectDetail.audioRecorder.microphoneReceiving')}
										</StatusWord>
									) : (
										<StatusWord tone="waiting">
											{t('pages.projectDetail.audioRecorder.meetingAudioShareGateHint')}
										</StatusWord>
									)}
									{!isRecordingPhase ? (
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className={iconButtonClassName}
											onClick={() => onIncludeMeetingAudioChange(false)}
											aria-label={t('pages.projectDetail.audioRecorder.removeMeetingAudioLabel')}
										>
											<X className="size-4" />
										</Button>
									) : null}
								</>
							) : (
								<Button
									type="button"
									variant="outline"
									className={textButtonClassName}
									disabled={!meetingSupported || isRecordingPhase}
									onClick={() => onIncludeMeetingAudioChange(true)}
								>
									{t('pages.projectDetail.audioRecorder.meetingAudioLevelLabel')}
								</Button>
							)}
						</div>

						{includeMeetingAudio ? (
							<>
								{!isRecordingPhase ? (
									<>
										<p className="text-sm text-muted-foreground">
											<Trans
												i18nKey="pages.projectDetail.audioRecorder.meetingAudioInstructionsDesktop"
												components={transStrong}
											/>
										</p>
										{!meetingPreviewActive ? (
											<Button
												type="button"
												variant="outline"
												className={cn(textButtonClassName, 'gap-2')}
												disabled={meetingPreviewConnecting || !meetingSupported}
												onClick={() => void onStartMeetingPreview()}
											>
												<Share2 className="size-4 shrink-0" aria-hidden />
												{t('pages.projectDetail.audioRecorder.meetingAudioShareButton')}
											</Button>
										) : null}
									</>
								) : null}
								<LevelMeter
									percent={meetingPreviewActive ? meetingPreviewMeterPercent : 4}
									tone={meetingMeterTone}
								/>
							</>
						) : null}
					</div>
				</div>
			</div>

			<div className="flex flex-nowrap items-center gap-2 border-t border-border bg-muted/30 px-4 py-3">
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className={iconButtonClassName}
							aria-label={t('pages.projectDetail.audioRecorder.microphonePickerHint')}
						>
							<Info className="size-4" />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>{t('pages.projectDetail.audioRecorder.meetingAudioHeadphonesHint')}</p>
					</TooltipContent>
				</Tooltip>

				<div aria-live="polite" className="min-w-0 flex-1">
					{isRecordingPhase ? (
						<span className="sr-only">{liveAnnouncement}</span>
					) : footerReason ? (
						<p className="whitespace-nowrap text-[11px] leading-4 tracking-tight text-muted-foreground">
							{footerReason}
						</p>
					) : (
						<span className="sr-only">{liveAnnouncement}</span>
					)}
				</div>

				{isRecording || isPaused ? (
					<div className="flex shrink-0 items-center gap-2">
						{pauseSupported ? (
							isRecording ? (
								<Button
									type="button"
									variant="outline"
									className={cn(textButtonClassName, 'gap-2')}
									onClick={onPause}
								>
									<Pause className="size-4" aria-hidden />
									{t('pages.projectDetail.audioRecorder.pause')}
								</Button>
							) : (
								<Button
									type="button"
									variant="outline"
									className={cn(textButtonClassName, 'gap-2')}
									onClick={onResume}
								>
									<Play className="size-4" aria-hidden />
									{t('pages.projectDetail.audioRecorder.resume')}
								</Button>
							)
						) : null}
						<Button
							type="button"
							className={cn(
								textButtonClassName,
								'gap-2 bg-brand-record-stop text-white hover:bg-brand-record-stop-hover'
							)}
							onClick={onStop}
						>
							<Square className="size-4" aria-hidden />
							{t('pages.projectDetail.audioRecorder.stop')}
						</Button>
					</div>
				) : (
					<div className="flex shrink-0 items-center gap-2">
						{includeMeetingAudio && !meetingPreviewActive && !micPreviewFailed ? (
							<Button
								type="button"
								variant="ghost"
								className={textButtonClassName}
								onClick={() => onIncludeMeetingAudioChange(false)}
							>
								{t('pages.projectDetail.audioRecorder.nextButton')}
							</Button>
						) : null}
						<Button
							type="button"
							disabled={!canRecord}
							className={cn(
								textButtonClassName,
								canRecord
									? 'bg-brand-accent text-white hover:bg-brand-accent-hover'
									: 'bg-muted text-muted-foreground disabled:opacity-100'
							)}
							onClick={() => void onRecord()}
						>
							{state === 'requesting'
								? t('pages.projectDetail.audioRecorder.starting')
								: t('pages.projectDetail.audioRecorder.start')}
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}
